import React, { useMemo } from 'react';
import { IconButton, Tooltip as MuiTooltip } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { sub } from 'date-fns';

const DEFAULT_PRESETS = [
    { label: 'All', unit: null },
    { label: '1y', unit: { years: 1 } },
    { label: '1m', unit: { months: 1 } },
    { label: '1w', unit: { weeks: 1 } },
    { label: '1d', unit: { days: 1 } },
];

function DatePick(props) {
    return (
        <DatePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy MMMM d.'
            cancelLabel='Vissza'
            okLabel='Ok'
            clearLabel='Törlés'
            inputVariant='outlined'
            clearable
        />
    );
}

export default function DateRangeToolbar({
    from,
    to,
    minDate,
    maxDate,
    onChange,
    presets = DEFAULT_PRESETS,
    className = '',
    dense = false,
}) {
    const canUsePresets = useMemo(() => Boolean(minDate && maxDate), [minDate, maxDate]);

    const isPresetAvailable = (unit) => {
        if (!canUsePresets) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    };

    const handlePreset = (unit) => {
        if (!canUsePresets) return;
        if (!unit) {
            onChange({ from: minDate, to: maxDate });
            return;
        }
        const newFrom = sub(maxDate, unit);
        const clampedFrom = (minDate && newFrom < minDate) ? minDate : newFrom;
        onChange({ from: clampedFrom, to: maxDate });
    };

    const handleFromChange = (v) => {
        let nextFrom = v ? new Date(v) : null;
        let nextTo = to ? new Date(to) : null;

        if (minDate && nextFrom && nextFrom < minDate) nextFrom = minDate;
        if (maxDate && nextFrom && nextFrom > maxDate) nextFrom = maxDate;

        if (nextFrom && nextTo && nextFrom > nextTo) {
            nextTo = nextFrom;
        }
        onChange({ from: nextFrom, to: nextTo });
    };

    const handleToChange = (v) => {
        let nextTo = v ? new Date(v) : null;
        let nextFrom = from ? new Date(from) : null;

        if (maxDate && nextTo && nextTo > maxDate) nextTo = maxDate;
        if (minDate && nextTo && nextTo < minDate) nextTo = minDate;

        if (nextFrom && nextTo && nextTo < nextFrom) {
            nextFrom = nextTo;
        }
        onChange({ from: nextFrom, to: nextTo });
    };

    return (
        <div className={'w-full flex flex-col gap-8 shadow-inner px-20 ' + (dense ? 'py-6' : 'py-14') + ' ' + className}>
            <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                <div className='flex-1 flex items-center gap-16'>
                    <DatePick
                        className='flex-1'
                        label='From'
                        value={from}
                        minDate={minDate}
                        maxDate={to || maxDate}
                        onChange={handleFromChange}
                    />
                    <DatePick
                        className='flex-1'
                        label='To'
                        value={to}
                        minDate={from || minDate}
                        maxDate={maxDate}
                        onChange={handleToChange}
                    />
                    <div className='flex gap-4 items-center'>
                        {presets.map(({ label, unit }) => (
                            <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow>
                                <span>
                                    <IconButton
                                        size='small'
                                        onClick={() => handlePreset(unit)}
                                        disabled={!isPresetAvailable(unit)}
                                    >
                                        <span className='text-xs font-semibold p-4'>{label}</span>
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}